import {
	HubConnection,
	HubConnectionBuilder,
	LogLevel,
} from "@microsoft/signalr";
import { Send as SendIcon } from "@mui/icons-material";
import {
	Box,
	Button,
	Chip,
	Grid2,
	List,
	ListItem,
	ListItemText,
	Paper,
	TextField,
	Typography,
	useTheme,
} from "@mui/material";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { HelmetDexteel } from "../../../../helmet/HelmetDexteel";
import { ChatMessage, ConnectionState } from "./types";

const ChatComponent: React.FC = () => {
	const theme = useTheme();

	const [hubUrl, setHubUrl] = useState<string>("/ws");
	const [connection, setConnection] = useState<HubConnection | null>(null);
	const [connectionState, setConnectionState] = useState<ConnectionState>({
		isConnected: false,
	});
	const [messages, setMessages] = useState<ChatMessage[]>([]);
	const [user, setUser] = useState<string>("");
	const [message, setMessage] = useState<string>("");
	const messageEndRef = useRef<null | HTMLDivElement>(null);
	const connectionRef = useRef<HubConnection | null>(null);

	const latestMessages = useRef<ChatMessage[]>([]);
	latestMessages.current = messages;

	const scrollToBottom = () => {
		messageEndRef.current?.scrollIntoView({ behavior: "smooth" });
	};

	useEffect(() => {
		scrollToBottom();
	}, [messages]);

	const startConnection = useCallback(async () => {
		try {
			const newConnection = new HubConnectionBuilder()
				.withUrl(hubUrl)
				.withAutomaticReconnect()
				.configureLogging(LogLevel.Information)
				.build();

			connectionRef.current = newConnection;

			newConnection.on("ReceiveMessage", (user: string, message: string) => {
				const newMessage: ChatMessage = {
					user,
					message,
					timestamp: new Date(),
				};
				setMessages((prev) => [...prev, newMessage]);
			});

			newConnection.on("UserConnected", (connectionId: string) => {
				console.log(`User connected: ${connectionId}`);
				setConnectionState((prev) => ({
					...prev,
					isConnected: true,
					connectionId,
				}));
			});

			newConnection.on("UserDisconnected", (connectionId: string) => {
				console.log(`User disconnected: ${connectionId}`);
				if (connectionId === connectionState.connectionId) {
					setConnectionState((prev) => ({
						...prev,
						isConnected: false,
						connectionId: undefined,
					}));
				}
			});

			await newConnection.start();
			setConnection(newConnection);
			console.log("SignalR Connected");
		} catch (err) {
			console.error("SignalR Connection Error:", err);
			setConnectionState((prev) => ({
				...prev,
				isConnected: false,
				error: err as Error,
			}));
		}
	}, [hubUrl]);

	useEffect(() => {
		startConnection();
		return () => {
			void connectionRef.current?.stop();
			connectionRef.current = null;
		};
	}, [startConnection]);

	const sendMessage = async (e: React.FormEvent) => {
		e.preventDefault();
		if (!connection || !message || !user) return;

		try {
			await connection.invoke("SendMessage", user, message);
			setMessage("");
		} catch (err) {
			console.error("Error sending message:", err);
		}
	};

	return (
		<>
			<HelmetDexteel title="Client - Real Time" />
			<Paper
				sx={{
					width: "100%",
					maxWidth: 800,
					margin: "0 auto",
					padding: (theme) => theme.spacing(3),
				}}
				elevation={3}
			>
				<Box mb={2}>
					<Chip
						label={connectionState.isConnected ? "Connected" : "Disconnected"}
						color={connectionState.isConnected ? "primary" : "default"}
						sx={{
							marginBottom: theme.spacing(2),
						}}
					/>
					{connectionState.error && (
						<Typography color="error">
							Error: {connectionState.error.message}
						</Typography>
					)}
				</Box>

				<Paper
					sx={{
						height: 400,
						overflow: "auto",
						marginBottom: (theme) => theme.spacing(2),
						padding: (theme) => theme.spacing(2),
						backgroundColor: (theme) => theme.palette.background.default,
					}}
					variant="outlined"
				>
					<List>
						{messages.map((msg, index) => (
							<ListItem key={index}>
								<ListItemText
									primary={
										<Typography>
											<strong>{msg.user}:</strong> {msg.message}
										</Typography>
									}
									secondary={
										<span
											style={{
												color: theme.palette.text.secondary,
												fontSize: "0.8rem",
											}}
										>
											{msg.timestamp.toLocaleTimeString()}
										</span>
									}
								/>
							</ListItem>
						))}
						<div ref={messageEndRef} />
					</List>
				</Paper>

				<Grid2
					container
					size={{ md: 12 }}
					component="form"
					onSubmit={sendMessage}
					spacing={1}
					alignItems="center"
				>
					<Grid2 size={{ md: 5 }}>
						<TextField
							value={user}
							onChange={(e) => setUser(e.target.value)}
							label="Your name"
							variant="outlined"
							size="small"
						/>
					</Grid2>
					<Grid2 size={{ md: 5 }}>
						<TextField
							sx={{
								flexGrow: 1,
							}}
							value={message}
							onChange={(e) => setMessage(e.target.value)}
							label="Type a message"
							variant="outlined"
							size="small"
						/>
					</Grid2>
					<Grid2 size={{ md: 2 }}>
						<Button
							type="submit"
							variant="contained"
							color="primary"
							disabled={!connectionState.isConnected || !user || !message}
							endIcon={<SendIcon />}
							fullWidth
							size="medium"
						>
							Send
						</Button>
					</Grid2>
				</Grid2>
			</Paper>
		</>
	);
};

export default ChatComponent;
